import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import type { CombinationPriceGrid, SaveCombinationPricesRequest } from '@models/combination-prices';
import { Observable } from 'rxjs';

/**
 * Per-unit combination pricing (CS-285). The grid is exchanged as one document per (unit, price list):
 * a single GET loads all seasons × stay types × cells, and a single PUT saves the whole grid (the API
 * reconciles to minimal writes). Accommodations and fishing spots share the same grid shape and differ
 * only in the URL segment (`accommodations` vs `fishing-spots`).
 */
@Injectable({ providedIn: 'root' })
export class CrmCombinationPricesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api/crm/waters`;

  getAccommodationGrid(
    waterId: number,
    accommodationId: number,
    priceListId: number,
  ): Observable<CombinationPriceGrid> {
    return this.http.get<CombinationPriceGrid>(this.accommodationGridUrl(waterId, accommodationId, priceListId));
  }

  saveAccommodationGrid(
    waterId: number,
    accommodationId: number,
    priceListId: number,
    request: SaveCombinationPricesRequest,
  ): Observable<CombinationPriceGrid> {
    return this.http.put<CombinationPriceGrid>(
      this.accommodationGridUrl(waterId, accommodationId, priceListId),
      request,
    );
  }

  getFishingSpotGrid(waterId: number, spotId: number, priceListId: number): Observable<CombinationPriceGrid> {
    return this.http.get<CombinationPriceGrid>(this.fishingSpotGridUrl(waterId, spotId, priceListId));
  }

  saveFishingSpotGrid(
    waterId: number,
    spotId: number,
    priceListId: number,
    request: SaveCombinationPricesRequest,
  ): Observable<CombinationPriceGrid> {
    return this.http.put<CombinationPriceGrid>(this.fishingSpotGridUrl(waterId, spotId, priceListId), request);
  }

  private accommodationGridUrl(waterId: number, accommodationId: number, priceListId: number): string {
    return `${this.baseUrl}/${waterId}/accommodations/${accommodationId}/price-lists/${priceListId}/combination-prices`;
  }

  private fishingSpotGridUrl(waterId: number, spotId: number, priceListId: number): string {
    return `${this.baseUrl}/${waterId}/fishing-spots/${spotId}/price-lists/${priceListId}/combination-prices`;
  }
}
